import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { Metamodel } from 'app/model/base/model';
import { AzureDataExplorerCopySource } from 'app/model/pipeline/copysource/azure-data-explorer.copysource';
import { AzureDataExplorerCopySink } from 'app/model/pipeline/copysink/azure-data-explorer.copysink';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { GlobalVariables } from 'app/services/globals';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import {
    azureDefaultAuthRefTypes,
    mergeUamiCredentialOptionAdx,
    mergeUserAuthCredentialOption
} from '../utls/authenticationUtils';
import { adxAuthTypes, ConnectionAuthKind, CredentialTypeOptions } from 'shell-models-lib';
import { authenticationReferenceMethodOptions } from '../consts/metadata-consts';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { IDataflowBuildInTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';

const baseControlFunc: (isMIAuth: boolean) => ControlMetadata = (isMIAuth: boolean) => {
    return {
        type: 'azure-data-explorer-linkedservice',
        bind: {
            endpoint: 'endpoint',
            tenant: 'tenant',
            servicePrincipalId: 'servicePrincipalId',
            servicePrincipalKey: 'servicePrincipalKey',
            database: 'database'
        },
        isMIAuth,
        parameterizable: true
    };
};

const defaultAdxAuthenticationOption: Array<ISelectOption<string>> = [
    {
        label: stringResources.AuthenticationType_ServicePrincipal,
        value: adxAuthTypes.ServicePrincipalAuthentication
    },
    {
        label: stringResources.AuthenticationType_ManagedServiceIdentity,
        value: adxAuthTypes.MSIAuthentication
    }
];

// provides user with inline vs credential radio button to add authentication parameters for SAMI and SP Authentication methods.
function adxAuthReferenceSetting(credentialTypeFilter: CredentialTypeOptions): ControlMetadata {
    return {
        type: 'radio-switch',
        bind: 'authReferenceMethod',
        nested: 1,
        label: stringResources.Authentication_Reference_Method_Label,
        description: stringResources.Authentication_Reference_Method_Description,
        options: authenticationReferenceMethodOptions,
        isLabelVertical: true,
        defaultValue: azureDefaultAuthRefTypes.InlineRefTypeAuthentication,
        cases: [
            {
                caseValue: azureDefaultAuthRefTypes.InlineRefTypeAuthentication,
                children:
                    credentialTypeFilter === CredentialTypeOptions.ServicePrincipal
                        ? [
                            // service Principal
                            baseControlFunc(false)
                        ]
                        : [
                            // system Assigned Managed Identity
                            baseControlFunc(true),
                            {
                                type: 'management-identity-info',
                                resourceType: stringResources.AzureDataExplorer_DisplayText,
                                link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-data-explorer?tabs=data-factory#managed-identity'
                            }
                        ]
            },
            {
                caseValue: azureDefaultAuthRefTypes.CredentialRefTypeAuthentication,
                children: [
                    // if we use credential method, the isMIAuth flag should be true for SP along with MSI, since we don't need SPN details.
                    baseControlFunc(true),
                    {
                        type: 'credential-select',
                        bind: 'credential',
                        filterCredentialType: credentialTypeFilter,
                        supportFic: GlobalVariables.supportFic,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }]
                    }
                ]
            }
        ]
    };
}

const newControlMetadata: ControlMetadata = {
    type: 'select-switch',
    bind: 'authenticationMethod',
    label: stringResources.Authentication_Method_Label,
    options: mergeUserAuthCredentialOption(
        mergeUamiCredentialOptionAdx(defaultAdxAuthenticationOption),
        adxAuthTypes.UserAuth
    ),
    isLabelVertical: true,
    defaultValue: 'ServicePrincipalAuthentication',
    cases: [
        {
            caseValue: adxAuthTypes.ServicePrincipalAuthentication,
            children: [adxAuthReferenceSetting(CredentialTypeOptions.ServicePrincipal)]
        },
        {
            caseValue: adxAuthTypes.MSIAuthentication,
            children: !GlobalVariables.isSamiSupportEnabled
                ? [
                    baseControlFunc(true),
                    {
                        type: 'management-identity-info',
                        resourceType: stringResources.AzureDataExplorer_DisplayText,
                        link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-data-explorer?tabs=data-factory#managed-identity'
                    }
                ]
                : [adxAuthReferenceSetting(CredentialTypeOptions.ManagedIdentity)]
        },
        {
            caseValue: adxAuthTypes.UserAssignedManagedIdentityAuthentication,
            children: [
                baseControlFunc(true),
                {
                    type: 'credential-select',
                    bind: 'credential',
                    filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                    required: true,
                    validators: [{ type: 'required', errorMessage: '' }]
                }
            ]
        },
        {
            caseValue: adxAuthTypes.UserAuth,
            children: [
                baseControlFunc(true),
                {
                    type: 'credential-select',
                    bind: 'credential',
                    hide: !GlobalVariables.enableConnectorUserAuthCred,
                    filterCredentialType: CredentialTypeOptions.UserAuth,
                    required: true,
                    validators: [{ type: 'required', errorMessage: '' }]
                }
            ]
        }
    ]
};

const dataflowAdx: IDataflowBuildInTypeMetadata = {
    type: DatasetFormatType.ADX,
    settings: adfModelTypeMap.azureDataExplorer.dataflowSettings,
    useInlineProjection: true,
    enableImportSchemaInSink: true
};

export const onetimeQueryTemplate = '<<tableName>> | take 10';

export const azureDataExplorerMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.azureDataExplorer.linkedServiceType,
        displayName: __IS_FABRIC_DPE__
            ? stringResources.AzureDataExplorer_DisplayText
            : stringResources.AzureDataExplorer_DisplayText_old,
        linkedServiceKind: 'table',
        supportedInTrident: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: ResourceKind.AzureDataExplorer
        },
        supportOPDG: GlobalVariables.enableOPDG,
        supportedAuthKindsInTrident: [
            ConnectionAuthKind.OAuth2,
            ConnectionAuthKind.ServicePrincipal,
            ConnectionAuthKind.WorkspaceIdentity
        ],
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        supportMultipleIncrementalColumns: true,
        icon: {
            className: 'svg-i-100x100-azure_data_explorer'
        },
        isFirstClass: true,
        tableNameAlias: 'table',
        category: ['Azure'],
        supportDatasetV2: true,
        supportDataFlowSource: true,
        supportDataFlowSink: true,
        dataflowSource: [dataflowAdx],
        dataflowSink: [dataflowAdx],
        copySinkTypes: [Metamodel.modelFor(AzureDataExplorerCopySink).name],
        copySourceTypes: [Metamodel.modelFor(AzureDataExplorerCopySource).name],
        onetimeQueryTemplate,
        timedQueryTemplate:
            '<<tableName>> \n| where ' +
            '<<columnName>> >= datetime(@{formatDateTime(pipeline().parameters.windowStart)}) \nand <<columnName>> < datetime(@{formatDateTime(pipeline().parameters.windowEnd)})' +
            '\n| take 10',
        deltaLoadQueryTemplate: `<<tableName>> | where <<columnName>> > @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue,
        concat('datetime(', formatDateTime(json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue), ')'))}
        and <<columnName>> <= @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue,
        concat('datetime(', formatDateTime(activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue), ')'))}`,
        supportFirstClassParameterExperience: true,
        controls: [newControlMetadata]
    };

    return metadata;
};
